import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import type {
    ClienteDTO,
    CreateSiniestroRequest,
    PagaebleResult,
    PolizaDTO,
    SearchPolizaDTO,
    SiniestroDTO,
    SiniestroStatus,
    WrapperResponse,
} from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import { parseDate, todayIso } from '../../../utils/dates'
import { clip } from '../../../utils/inputs'
import { searchClientes, useLookupField } from '../../../utils/lookups'
import {
    aseguradoraName,
    CLAIMS_PATH,
    errorMessage,
    labelize,
    personName,
    ramoName,
    statusKey,
    vehicleLabel,
} from '../common/claim-common'
import type { ClaimLocationState } from '../common/claim-common'

export const NO_SINIESTRO_MAX = 50
export const DESCRIPCION_MAX = 1500

const LOAD_ERROR = 'No se pudo cargar el siniestro.'
const POLIZAS_ERROR = 'No se pudieron cargar las pólizas del cliente.'
const POLIZA_ERROR = 'No se pudo cargar el detalle de la póliza.'
const SAVE_ERROR = 'Error al guardar el siniestro'

export interface ClaimPolicyOption {
    id: number
    noPoliza: string | null
    aseguradora: string | null
    ramo: string | null
}

export interface ClaimInsuredOption {
    id: number
    name: string
    parentesco: string | null
    selected: boolean
}

export interface ClaimFormState {
    id: number | null
    noSiniestro: string
    cliente: ClienteDTO | null
    polizaId: number | ''
    fechaSiniestro: string
    fechaEstimadaResolucion: string
    descripcion: string
    status: SiniestroStatus
    asegurados: ClaimInsuredOption[]
}

export interface FormMessage {
    type: 'error' | 'success'
    message: string
}

const emptyState = (): ClaimFormState => ({
    id: null,
    noSiniestro: '',
    cliente: null,
    polizaId: '',
    fechaSiniestro: todayIso(),
    fechaEstimadaResolucion: '',
    descripcion: '',
    status: 'captura',
    asegurados: [],
})

const toPolicyOption = (item: SearchPolizaDTO): ClaimPolicyOption => ({
    id: item.polizaId,
    noPoliza: item.noPoliza,
    aseguradora: item.aseguradora,
    ramo: item.ramo,
})

const policyOptionFromDetail = (poliza: PolizaDTO): ClaimPolicyOption => ({
    id: poliza.id,
    noPoliza: poliza.noPoliza,
    aseguradora: aseguradoraName(poliza),
    ramo: ramoName(poliza.ramo) || null,
})

const toFormState = (body: SiniestroDTO): ClaimFormState => ({
    id: body.id,
    noSiniestro: body.noSiniestro || '',
    cliente: body.poliza?.cliente || null,
    polizaId: body.poliza?.id ?? '',
    fechaSiniestro: body.fechaSiniestro || '',
    fechaEstimadaResolucion: body.fechaEstimadaResolucion || '',
    descripcion: body.descripcion || '',
    status: body.status || 'captura',
    asegurados: (body.asegurados || []).map((item) => ({
        id: item.asegurado.id,
        name: personName(item.asegurado),
        parentesco: labelize(item.parentesco),
        selected: item.selected,
    })),
})

const fetchPolizasByCliente = async (clienteId: number) => {
    const response = await APIInvoke.invokeGET(`/polizas?size=50&status=Vigente&order=fechaVigenciaA%20ASC&clienteId=${clienteId}`)
    const payload: WrapperResponse<PagaebleResult<SearchPolizaDTO>> | null = await response.json()
    if (!payload?.ok) throw new Error(payload?.message || POLIZAS_ERROR)
    return (payload.body?.items || []).map(toPolicyOption)
}

const fetchPoliza = async (polizaId: number) => {
    const response = await APIInvoke.invokeGET(`/polizas/${polizaId}`)
    const payload: WrapperResponse<PolizaDTO> | null = await response.json()
    if (!payload?.ok || !payload.body) throw new Error(payload?.message || POLIZA_ERROR)
    return payload.body
}

const isBefore = (value: string, reference: string) => {
    const date = parseDate(value)
    const ref = parseDate(reference)
    return Boolean(date && ref && date < ref)
}

export const useClaimFormModel = () => {
    const history = useHistory()
    const location = useLocation()
    const { siniestroId } = useParams()
    const backUrl = (location.state as ClaimLocationState | null)?.backUrl || CLAIMS_PATH
    const polizasRequest = useRef(0)
    const polizaRequest = useRef(0)

    const [state, setState] = useState<ClaimFormState>(emptyState)
    const [poliza, setPoliza] = useState<PolizaDTO | null>(null)
    const [polizas, setPolizas] = useState<ClaimPolicyOption[]>([])
    const [loading, setLoading] = useState(false)
    const [loadingPolizas, setLoadingPolizas] = useState(false)
    const [loadingPoliza, setLoadingPoliza] = useState(false)
    const [message, setMessage] = useState<FormMessage | null>(null)

    const patch = (partial: Partial<ClaimFormState>) => setState((current) => ({ ...current, ...partial }))

    const loadPolizas = async (clienteId: number, current?: ClaimPolicyOption) => {
        const request = ++polizasRequest.current
        try {
            setLoadingPolizas(true)
            const items = await fetchPolizasByCliente(clienteId)
            if (request !== polizasRequest.current) return
            setPolizas(current && !items.some((item) => item.id === current.id) ? [current, ...items] : items)
        } catch (error) {
            if (request !== polizasRequest.current) return
            setPolizas(current ? [current] : [])
            setMessage({ type: 'error', message: errorMessage(error, POLIZAS_ERROR) })
        } finally {
            if (request === polizasRequest.current) setLoadingPolizas(false)
        }
    }

    useEffect(() => {
        if (!siniestroId) return undefined
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setMessage(null)
                const response = await APIInvoke.invokeGET(`/siniestros/${siniestroId}`)
                const payload: WrapperResponse<SiniestroDTO> | null = await response.json()
                if (!active) return
                if (!payload?.ok || !payload.body) throw new Error(payload?.message || LOAD_ERROR)
                const body = payload.body
                setState(toFormState(body))
                setPoliza(body.poliza)
                if (body.poliza) {
                    const current = policyOptionFromDetail(body.poliza)
                    setPolizas([current])
                    if (body.poliza.cliente?.id) loadPolizas(body.poliza.cliente.id, current)
                }
            } catch (error) {
                if (active) setMessage({ type: 'error', message: errorMessage(error, LOAD_ERROR) })
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siniestroId])

    const changeCliente = (cliente: ClienteDTO | null) => {
        polizaRequest.current += 1
        patch({ cliente, polizaId: '', asegurados: [] })
        setPoliza(null)
        setLoadingPoliza(false)
        setPolizas([])
        if (cliente?.id) loadPolizas(cliente.id)
        else {
            polizasRequest.current += 1
            setLoadingPolizas(false)
        }
    }

    const clienteLookup = useLookupField<ClienteDTO>({ value: state.cliente, onChange: changeCliente, search: searchClientes })

    const changePoliza = async (value: number | '') => {
        const request = ++polizaRequest.current
        patch({ polizaId: value, asegurados: [] })
        setPoliza(null)
        if (!value) {
            setLoadingPoliza(false)
            return
        }
        try {
            setLoadingPoliza(true)
            const detail = await fetchPoliza(value)
            if (request !== polizaRequest.current) return
            setPoliza(detail)
            patch({
                asegurados: (detail.asegurados || [])
                    .filter((item) => item.id != null)
                    .map((item) => ({
                        id: Number(item.id),
                        name: personName(item),
                        parentesco: labelize(item.parentesco),
                        selected: false,
                    })),
            })
        } catch (error) {
            if (request === polizaRequest.current) setMessage({ type: 'error', message: errorMessage(error, POLIZA_ERROR) })
        } finally {
            if (request === polizaRequest.current) setLoadingPoliza(false)
        }
    }

    const toggleAsegurado = (id: number, selected: boolean) => setState((current) => ({
        ...current,
        asegurados: current.asegurados.map((item) => (item.id === id ? { ...item, selected } : item)),
    }))

    const change = {
        noSiniestro: (value: string) => patch({ noSiniestro: clip(value, NO_SINIESTRO_MAX) }),
        fechaSiniestro: (value: string) => patch({ fechaSiniestro: value }),
        fechaEstimadaResolucion: (value: string) => patch({ fechaEstimadaResolucion: value }),
        descripcion: (value: string) => patch({ descripcion: clip(value, DESCRIPCION_MAX) }),
        status: (value: SiniestroStatus) => patch({ status: value }),
        cliente: changeCliente,
        poliza: changePoliza,
        asegurado: toggleAsegurado,
    }

    const ramo = ramoName(poliza?.ramo) || null
    const ramoKey = statusKey(ramo)
    const showAsegurados = ramoKey === 'vida' || ramoKey === 'salud'
    const resolucionAntes = Boolean(state.fechaSiniestro && state.fechaEstimadaResolucion && isBefore(state.fechaEstimadaResolucion, state.fechaSiniestro))

    const validate = () => {
        if (!state.cliente) return 'Selecciona el cliente.'
        if (!state.polizaId || !poliza) return 'Selecciona la póliza afectada.'
        if (!state.noSiniestro.trim()) return 'Captura el número de siniestro.'
        if (!state.fechaSiniestro || !state.fechaEstimadaResolucion) return 'Captura las fechas del siniestro.'
        if (resolucionAntes) return 'La fecha estimada de resolución no puede ser anterior a la del siniestro.'
        if (!state.descripcion.trim()) return 'Describe el siniestro.'
        if (showAsegurados && state.asegurados.length > 0 && !state.asegurados.some((item) => item.selected)) return 'Selecciona al menos un asegurado.'
        return null
    }

    const save = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        const invalid = validate()
        if (invalid || !state.polizaId) {
            setMessage({ type: 'error', message: invalid || SAVE_ERROR })
            return
        }
        try {
            setLoading(true)
            setMessage(null)
            const request: CreateSiniestroRequest = {
                id: state.id,
                polizaId: state.polizaId,
                noSiniestro: state.noSiniestro.trim(),
                fechaSiniestro: state.fechaSiniestro,
                fechaEstimadaResolucion: state.fechaEstimadaResolucion,
                descripcion: state.descripcion.trim(),
                status: state.status,
                asegurados: state.asegurados.filter((item) => item.selected).map((item) => item.id),
            }
            const response: WrapperResponse<SiniestroDTO> = state.id
                ? await (await APIInvoke.invokePUT(`/siniestros/${state.id}`, request)).json()
                : await (await APIInvoke.invokePOST('/siniestros', request)).json()

            if (response.ok && response.body) {
                const nextState: ClaimLocationState = { detailId: response.body.id, backUrl: CLAIMS_PATH }
                history.push({ pathname: CLAIMS_PATH, state: nextState })
            } else {
                setMessage({ type: 'error', message: response.message || SAVE_ERROR })
            }
        } catch (error) {
            console.error('error al guardar el siniestro', error)
            setMessage({ type: 'error', message: SAVE_ERROR })
        } finally {
            setLoading(false)
        }
    }

    const isEdit = Boolean(siniestroId || state.id)

    const polizaHelp = (() => {
        if (!state.cliente) return 'Primero elige el cliente'
        if (loadingPolizas) return 'Cargando pólizas…'
        if (polizas.length === 0) return 'El cliente no tiene pólizas vigentes'
        if (loadingPoliza) return 'Cargando detalle de la póliza…'
        return 'Solo se listan pólizas vigentes'
    })()

    return {
        form: state,
        poliza,
        polizas,
        loading,
        loadingPolizas,
        loadingPoliza,
        busyLabel: siniestroId && !state.id ? 'Cargando siniestro…' : 'Guardando siniestro…',
        message,
        title: isEdit ? 'Editar siniestro' : 'Nuevo siniestro',
        isEdit,
        backTo: backUrl,
        clienteLookup,
        ramo,
        showAsegurados,
        contratante: poliza?.cliente ? personName(poliza.cliente) : '',
        aseguradora: aseguradoraName(poliza),
        polizaHelp,
        vehiculo: ramoKey === 'auto' ? poliza?.vehiculo || null : null,
        missingVehiculo: ramoKey === 'auto' && !poliza?.vehiculo,
        vehiculoLabel: vehicleLabel(poliza?.vehiculo),
        bienTexto: ramoKey === 'diversos' ? poliza?.texto || null : null,
        resolucionAntes,
        change,
        save,
        cancel: () => history.push(backUrl),
    }
}
